<script lang="ts">
  import Fooji, { type UserDataInput } from '@fooji/microsites';
  import Input from './Input/Input.svelte';
  import { copy } from '../lib/stores/copy';
  import { FieldsValidationHelper, limitNumberFields } from '../lib/helpers/validators';
  import isEmpty from 'lodash/isEmpty';
  import type { SupportedCountryCode } from '../lib/constants/countries';
  import InvalidFieldMessage from './Input/InvalidFieldMessage.svelte';
  import { notify } from '../lib/helpers/bugsnag';
  import { isDemo } from '../lib/stores/mode';
  import RateLimitedModal from './RateLimitedModal.svelte';
  import { MonthOptions, InputLabels } from '../lib/helpers/checkAge';
  import Select from './Input/Select.svelte';
  import { validateDate, calculateAgeDetails } from '../lib/helpers/checkAge';
  import { languageStore } from '../lib/stores/language';
  import { countryStore } from '../lib/stores/country';
  import { setPage, nextStep, setLoading } from '../lib/stores/route';
  import { registrationStore } from '../lib/stores/registration';
  import { verifyStore } from '../lib/stores/verify';
  import { isPrimaryCampaign } from '../lib/constants/countries';
  import { labeledCheckboxes } from '../lib/constants/checkboxes';
  import { getUserId } from '../lib/helpers/auth';

  let skillAnswer = '';
  let formTitle: HTMLDivElement;
  let checkboxesDiv: HTMLDivElement;

  let skillQuestion: string;
  let skillExpectation: number;

  let validateOnChange = false;
  let showRateLimitedModal = false;

  const query = new URLSearchParams(document.location.search);
  if (isDemo() && query.has("showRateLimitedModal")) {
    showRateLimitedModal = true;
  }

  let fieldMessages: {
    firstName?: string;
    lastName?: string;
    email?: string;
    skill?: string;
    consoleOption?: string;
    dob?: string;
  } = {};

  const validateDob = () => {
    const { day, month, year } = $registrationStore.dob;

    if (!day || !month || !year) {
      fieldMessages.dob = $copy.registration.validations.dobInvalid;
      return;
    }

    const isValidDate = validateDate({ month, day, year });

    if (!isValidDate) {
      fieldMessages.dob = $copy.registration.validations.dobInvalid;
      return;
    }

    const { age } = calculateAgeDetails({ month, day, year });

    if (age < $copy.registration.minimumAge) {
      localStorage.setItem("underage", new Date().toISOString());
      fieldMessages.dob = $copy.registration.validations.dobUnderage; // so it blocks the api call from happening
      setPage("error", "underage");
    }
  };

  let checkboxMessages: Record<string, string> = {};

  const toggleCheckbox = (identifier: string) => () => {
    $registrationStore.checkboxStatuses[identifier] = !$registrationStore.checkboxStatuses[identifier];

    if (validateOnChange) {
      validate();
    }
  };

  const validateCheckboxes = () => {
    checkboxMessages =
      $labeledCheckboxes.reduce((acc: Record<string, string>, { identifier, mandatory }) => {
        if (mandatory && !$registrationStore.checkboxStatuses[identifier]) {
          acc[identifier] = $copy.registration.validations.mandatoryCheckbox;
        }
        
        return acc;
      }, {});
  };

  const skillTestGenerator = () => {
    const a = Math.floor(Math.random() * 1000);
    const b = Math.floor(Math.random() * 1000);

    // should not use eval due to the CSP
    skillExpectation = a + b;

    return `${a} + ${b} = ?`;
  };

  const validate = () => {
    const { data, issues } = FieldsValidationHelper.validateAllFields(
      $registrationStore,
      $copy.registration.validations
    );

    fieldMessages = issues ?? {};

    validateCheckboxes();
    validateDob();

    if (['MX', 'CA'].includes($countryStore!) && skillExpectation !== parseInt(skillAnswer, 10)) {
      fieldMessages.skill = $copy.registration.validations.invalidSkillAnswer;
    }

    if (isPrimaryCampaign && isEmpty($registrationStore.consoleOption)) {
      fieldMessages.consoleOption = $copy.registration.validations.invalidConsoleOption;
    }

    validateOnChange = true;

    return {
      first_name: data?.firstName || '',
      last_name: data?.lastName || '',
      email: data?.email || ''
    };
  }

  const saveAndProceed = async (data: UserDataInput) => {
    const { consoleOption, checkboxStatuses, dob: dobComponents } = $registrationStore;
    const { dob } = calculateAgeDetails(dobComponents);

    const commonUpdates: Record<string, string> = { dob };
    const additionalSettings: Record<string, number> = {};

    const checkboxStatusesToSave = {
      ...checkboxStatuses,
      receiveCommercialInfo: isPrimaryCampaign ? checkboxStatuses.receiveCommercialInfo : false
    }
    
    if (isPrimaryCampaign) { 
      commonUpdates.console_platform = consoleOption;
    } else {
      const userId = getUserId();
      
      additionalSettings.prize_1 = userId;
      additionalSettings.prize_2 = userId;
      additionalSettings.prize_3 = userId;
    }

    await Promise.all([
      Fooji.User.update({ ...data!, ...commonUpdates, language: $languageStore }),
      Fooji.UserSettings.save({ ...checkboxStatusesToSave, ...commonUpdates, ...additionalSettings, country: $countryStore })
    ]);
    
    nextStep();
  }

  const onSubmit = async () => {
    const data = validate();

    if (!isEmpty(fieldMessages)) {
      formTitle.scrollIntoView({ behavior: "smooth" });
      return;
    }

    if (!isEmpty(checkboxMessages)) {
      checkboxesDiv.scrollIntoView({ behavior: "smooth" });
      return;
    }

    setLoading(true);

    // TODO: We might need to try and pull the user data and fill it up on the form before all this
    // TODO: do we need to lock the fields that cannot change?
  
    // TODO: we need to support merging accounts
    // 1. we login, then we fetch the user
    // 2. we check if the user has the email set
    // 2.1. if it does, we just update the bits that matter normally (no email, no phone)
    // 2.2. if it doesn't, we attempt to update the user identity first
    // 2.2.1. if the call fails, we check if it's 409 or ValidationError with the phone validation error and trigger the validation flow
    // 2.2.2. if the call fails for other reasons... throw to some error page (this shouldn't happen normally, though)
    // 2.2.3. if the call succeeds, all done

    // Validation flow: the user sets the code and we call merge account with the email and code

    try {
      if (isDemo()) {
        if (query.has("viaVerify")) {
          $verifyStore.onVerifyCallback = () => nextStep();
          $verifyStore.verificationData = {
            channelUsed: "email",
            nextTryAt: new Date(Date.now() + 1000 * 60).toISOString(),
            last4PhoneNumber: ""
          };
          return;
        } else {
          return nextStep();
        }
      }

      const updateIdentityResponse = await Fooji.Auth.verify.updateIdentity({
        email: data.email,
        phone: "",
        channel: "email"
      });

      const { status } = updateIdentityResponse;

      if (status === "updated-successfully") {
        // New users only, as I believe we just trust the data
        await saveAndProceed(data);
      } else if (status === "needs-verification") {
        // TODO: We need to run the verification to then merge the account only after that we can proceed
        // Make it show the modal with the verification stuff
        // have "await saveAndProceed(data);" to run when the modal process finishes
        $verifyStore.onVerifyCallback = () => saveAndProceed(data);
        $verifyStore.verificationData = updateIdentityResponse.data;
      } else if (status === "rate-limited") {
        // Show rate limited modal
        showRateLimitedModal = true;
        return;
      } else {
        console.error('Failed to update identity', updateIdentityResponse);
        notify({ errorMessage: 'Failed to update identity', error: updateIdentityResponse });
        return setPage("error");
      }
    } catch (error) {
      notify({ errorMessage: 'Failed to submit registration', error });
      console.error('Error submitting registration:', error);
      setPage("error");
    } finally {
      setLoading(false);
    }
  };

  const setConsoleOption = (value: string) => {
    $registrationStore.consoleOption = value;

    if (validateOnChange) {
      validate();
    }
  };

  $: skillQuestion = skillTestGenerator();
  $: $registrationStore.dob.day = limitNumberFields($registrationStore.dob.day, 31);
  $: $registrationStore.dob.year = limitNumberFields($registrationStore.dob.year, new Date().getFullYear());
  $: country = $countryStore as SupportedCountryCode;
</script>

<div class="relative z[-1]">
  <div class="max-w-[1100px] mx-auto">
    <div class="bg-black/50 rounded-[12px] px-[16px] py-[32px] mobileS:p-[32px] tabletLg:px-[64px] tabletLg:mb-16">
      <div class="w-full">
        <div bind:this={formTitle}></div>
        <div class="flex justify-between flex-wrap">
          <Input
            bind:value={$registrationStore.firstName}
            identifier="first-name"
            label={$copy.registration.firstNameLabel}
            invalidMessage={fieldMessages.firstName}
            onChange={validateOnChange ? validate : () => {}} />
          <Input
            bind:value={$registrationStore.lastName}
            identifier="last-name"
            label={$copy.registration.lastNameLabel}
            invalidMessage={fieldMessages.lastName}
            onChange={validateOnChange ? validate : () => {}} />
        </div>
        <div class="flex justify-between flex-wrap">
          <Input
            bind:value={$registrationStore.email}
            identifier="email"
            label={$copy.registration.emailLabel}
            invalidMessage={fieldMessages.email}
            type="email"
            onChange={validateOnChange ? validate : () => {}}
            isDisabled={$registrationStore.lockedEmail} />
          <div class="relative w-full mb-[26px] tablet:w-[48%]">
            <label
              for="dob"
              class="font-MrEavesXLSanNarOTUltra relative text-[11px] inline-flex font-black text-primary mb-[4px] leading-[1.2] uppercase mobile:text-[12px] tablet:text-[12px] laptop:text-[15px]"
            >
              {$copy.registration.dobLabel}<span class="reg text-[0.7em] align-top">*</span>
            </label>
            <div id="dob" class="flex flex-col tablet:flex-row gap-4">
              <div class="std-select-wrap relative w-full tablet:flex-1">
                <Select
                  bind:value={$registrationStore.dob.month}
                  optionList={$MonthOptions}
                  placeholder={$InputLabels.month}
                  showPlaceholderOption={true} />
              </div>
              <div class="w-full tablet:w-20">
                <Input
                  includeMargin={false}
                  ignoreTabletW48={true}
                  identifier="day"
                  type="number"
                  inputProps={{ min: 1, max: 31 }}
                  bind:value={$registrationStore.dob.day}
                  placeholder={$InputLabels.day}
                  isMandatory={false} />
              </div>
              <div class="w-full tablet:w-24">
                <Input
                  includeMargin={false}
                  ignoreTabletW48={true}
                  identifier="year"
                  type="number"
                  inputProps={{ max: new Date().getFullYear() }}
                  bind:value={$registrationStore.dob.year}
                  placeholder={$InputLabels.year}
                  isMandatory={false} />
              </div>
            </div>
            <InvalidFieldMessage message={fieldMessages.dob} />
          </div>
        </div>
        <div class="flex justify-between flex-wrap">
          {#if isPrimaryCampaign}
            <div class="relative w-full mb-[26px] tablet:w-[48%]">
              <label for="user_gaming_console" class="font-MrEavesXLSanNarOTUltra relative text-[11px] inline-flex font-black text-primary mb-[4px] leading-[1.2] uppercase mobile:text-[12px] tablet:text-[12px] laptop:text-[15px]">
                {@html $copy.registration.consolePreference}&nbsp;<span class="reg text-[0.7em] align-top">*</span>
              </label>
              <div class="std-select-wrap relative">
                <Select
                  placeholder={$copy.registration.consolePreferencePlaceholder}
                  value={$registrationStore.consoleOption}
                  optionList={$copy.registration.consolePreferenceOptions}
                  onChange={setConsoleOption} />
                <div class="font-MrEavesXLSanNarOT text-[11px] absolute top-[110%] left-0 block text-primary mb-[4px] leading-[1.2]">
                  <i>{$copy.registration.notGuaranteed}</i>
                </div>
                <InvalidFieldMessage message={fieldMessages.consoleOption} />
              </div>
            </div>
          {/if}
          {#if ['MX', 'CA'].includes(country)}
            <Input
              bind:value={skillAnswer}
              identifier="skill"
              label={`${$copy.registration.skillLabel} ${skillQuestion}`}
              invalidMessage={fieldMessages.skill}
              type="text"
              inputProps={{ autocomplete: "off" }} />
          {/if}
        </div>
        <div bind:this={checkboxesDiv} class="checkboxes-list">
          {#each $labeledCheckboxes as { identifier, label, mandatory }}
            <div class="relative mb-[24px]">
              <label for="verify_years" id="{identifier}-switch" class="relative flex cursor-pointer">
                <input
                  class="std-switch__input absolute w-1 h-1 opacity-0"
                  aria-describedby={$copy.registration.ariaLabels.checkboxDescribedBy}
                  type="checkbox"
                  name={identifier}
                  id={identifier}
                  checked={$registrationStore.checkboxStatuses[identifier]}
                >
                <button on:click|preventDefault={toggleCheckbox(identifier)} class="std-switch__inner relative w-[52px] h-[24px] shrink-0 rounded-[32px] bg-primary overflow-hidden tablet:w-[62px] tablet:h-[28px]">
                  &nbsp;
                </button>
                <span class="std-switch__text font-MrEavesXLSanNarOT text-[13px] text-white ml-[4px] mt-[4px] mobileS:text-[14px] tablet:text-[15px] mobileS:ml-[8px] select-none">
                  {@html label}
                  {#if mandatory}
                    <span class="reg text-[0.7em] align-top">*</span>
                  {/if}
                </span>
                <InvalidFieldMessage slightlyOver={false} message={checkboxMessages[identifier]} />
              </label>
            </div>
          {/each}
        </div>
        <div class="flex items-center justify-center mt-[32px]">
          <button
            class="std-button font-MrEavesXLSanNarOTUltra relative inline-flex justify-center items-center text-[18px] text-white px-[32px] uppercase h-[48px] min-w-[132px] bg-dark border-solid border-white border-2 no-underline uppercase transition cursor-pointer overflow-hidden"
            type="submit"
            aria-label={$copy.registration.ariaLabels.submitButton}
            on:click|preventDefault={onSubmit}
          >
            <div class="relative z-[1]">{$copy.registration.submitButton}</div>
          </button>
        </div>
      </div>
    </div>
  </div>
</div>

<RateLimitedModal bind:isOpen={showRateLimitedModal} />
